import { html, css, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';
import { buttonStyles } from '../styles/button-styles.js';
import { formStyles } from '../styles/form-styles.js';

/**
 * Conductor login panel for the landing page.
 * Handles username/password form and Google OAuth button.
 *
 * @fires login - Fired when login form is submitted with { username, password }
 * @fires google-login - Fired when Google login button is clicked
 */
@customElement('conductor-login')
export class ConductorLogin extends BaseElement {
    static styles = [
        BaseElement.styles,
        buttonStyles,
        formStyles,
        css`
            :host {
                display: flex;
                flex-direction: column;
                align-items: center;
                flex: 1;
                border-radius: 1rem;
                padding: 1.5rem;
            }

            h2 {
                font-size: 1.5rem;
                margin: 0 0 0.5rem;
                text-align: center;
            }

            .panel-subtitle {
                font-size: 0.875rem;
                color: var(--text-secondary);
                text-align: center;
                margin-bottom: 1.5rem;
            }

            .form-group {
                max-width: 300px;
            }

            .btn {
                width: 100%;
                max-width: 300px;
                display: block;
            }

            .oauth-divider {
                display: flex;
                align-items: center;
                width: 100%;
                max-width: 300px;
                margin: 1rem 0;
            }

            .oauth-divider::before,
            .oauth-divider::after {
                content: '';
                flex: 1;
                height: 1px;
                background-color: var(--border);
            }

            .oauth-divider span {
                padding: 0 1rem;
                color: var(--text-secondary);
                font-size: 0.875rem;
                text-transform: lowercase;
            }

            .google-login-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 0.75rem;
                width: 100%;
                max-width: 300px;
                padding: 0.75rem 1rem;
                font-size: 0.9375rem;
                font-weight: 500;
                background-color: white;
                color: #3c4043;
                border: 1px solid #dadce0;
                border-radius: 0.375rem;
                cursor: pointer;
                transition: all 0.2s;
                font-family: inherit;
            }

            .google-login-btn:hover {
                background-color: #f8f9fa;
                border-color: #d2d4d8;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }

            .google-login-btn:active {
                background-color: #f1f3f4;
            }

            .google-login-btn svg {
                flex-shrink: 0;
            }

            @media (min-width: 768px) {
                .form-group {
                    max-width: 100%;
                }

                .btn {
                    max-width: 100%;
                }
            }
        `,
    ];

    @property({ type: String })
    error = '';

    @property({ type: Boolean })
    loading = false;

    @query('#auth-username')
    private _usernameInput!: HTMLInputElement;

    @query('#auth-password')
    private _passwordInput!: HTMLInputElement;

    private _onLogin(): void {
        const username = this._usernameInput?.value.trim();
        const password = this._passwordInput?.value;

        if (!username || !password) {
            this.error = 'Please enter both username and password';
            return;
        }

        this.dispatchEvent(
            new CustomEvent('login', {
                bubbles: true,
                composed: true,
                detail: { username, password },
            })
        );
    }

    private _onPasswordKeypress(e: KeyboardEvent): void {
        if (e.key === 'Enter') {
            this._onLogin();
        }
    }

    private _onGoogleLogin(): void {
        this.dispatchEvent(
            new CustomEvent('google-login', {
                bubbles: true,
                composed: true,
            })
        );
    }

    /** Clear password field and focus it (called by parent on auth error) */
    clearPassword(): void {
        if (this._passwordInput) {
            this._passwordInput.value = '';
            this._passwordInput.focus();
        }
    }

    render() {
        return html`
            <h2>Conductor Login</h2>
            <p class="panel-subtitle">Authenticate to start a new session</p>

            <div class="form-group">
                <label for="auth-username">Username:</label>
                <input
                    type="text"
                    id="auth-username"
                    placeholder="Enter username"
                    autocomplete="username"
                >
            </div>

            <div class="form-group">
                <label for="auth-password">Password:</label>
                <input
                    type="password"
                    id="auth-password"
                    placeholder="Enter password"
                    autocomplete="current-password"
                    @keypress=${this._onPasswordKeypress}
                >
            </div>

            ${this.error
                ? html`<p class="error-message">${this.error}</p>`
                : nothing}

            <button
                class="btn btn-secondary"
                @click=${this._onLogin}
                ?disabled=${this.loading}
            >
                ${this.loading ? 'Authenticating...' : 'Login as Conductor'}
            </button>

            <div class="oauth-divider">
                <span>or</span>
            </div>

            <button class="google-login-btn" @click=${this._onGoogleLogin}>
                <svg viewBox="0 0 24 24" width="18" height="18">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
            </button>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'conductor-login': ConductorLogin;
    }
}
